














import { Vue, Component } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { Breadcrumb } from "@brankas/web-ui";

import {
  ClientInfo,
  PersonInfo,
  SubmitFinancePICRequest,
  TFileUpload,
} from "@/types";

import EditTemplate from "@/pagesCom/ClientInfo/EditTemplate.vue";
import EditPICForm, {
  FormData,
  PersonInfoFormData,
} from "@/pagesCom/ClientInfo/EditPIC/index.vue";
import {
  ECountryCode,
  EDomicileTypes,
  EUserTypes,
} from "@/pagesCom/ClientInfo/index.dto";
import { PICType } from "@/proto/identity/identity/all_pb";
import {
  ESummaryContentType,
  IEditSummaryData,
} from "@/pagesCom/ClientInfo/EditSummary/index.dto";
import { initialFileTypeObj } from "./summary-utils";

const clientModule = namespace("client");
const configModule = namespace("config");

@Component({
  components: {
    EditTemplate,
    EditPICForm,
    Breadcrumb,
  },
})
export default class EditPIC extends Vue {
  @clientModule.State("currentClient") currentClient!: ClientInfo;
  @clientModule.Action("saveFinancePICForm") saveFinancePICForm!: (
    payload: SubmitFinancePICRequest
  ) => void;
  @clientModule.Action("removeFinancePICForm") removeFinancePICForm!: ({
    clientId,
    picId,
  }: {
    clientId: string;
    picId: string;
  }) => void;
  @configModule.Getter("isJKTDashboard") isJKTDashboard!: boolean;

  loading = false;
  showSummary = false;
  summaryData: any = null;
  errorMsg = null;

  get isPersonal() {
    return this.currentClient?.userType === EUserTypes.INDIVIDUAL;
  }

  get isLocalDomicile(): boolean {
    return this.currentClient.domicileType === EDomicileTypes.LOCAL;
  }

  _handleSummary(data: FormData): IEditSummaryData[] {
    const summary = [] as IEditSummaryData[];

    if (!this.isPersonal) {
      data.authorizedSignatory.forEach((as, index) => {
        const label =
          data.authorizedSignatory.length > 1
            ? `Authorized Signatory ${index + 1}`
            : "Authorized Signatory";
        summary.push({
          title: label,
          content: this._mappingContentSummary(as),
        });
      });
    }

    data.billingPIC.forEach((bill, index) => {
      const label =
        data.billingPIC.length > 1 ? `Billing PIC ${index + 1}` : "Billing PIC";
      summary.push({
        title: label,
        content: this._mappingContentSummary(bill),
      });
    });

    return summary;
  }

  _mappingContentSummary(personInfo: PersonInfoFormData) {
    return [
      {
        label: "Full Name",
        type: ESummaryContentType.TEXT,
        content: [personInfo.fullName],
      },
      {
        label: "Role",
        type: ESummaryContentType.TEXT,
        content: [personInfo.role],
      },
      {
        label: "Phone Number",
        type: ESummaryContentType.TEXT,
        content: [`${personInfo.phoneNumberCountryCode} ${personInfo.phoneNumber}`],
      },
      {
        label: "Email",
        type: ESummaryContentType.TEXT,
        content: [personInfo.email],
      },
      ...this.generateFileFields(personInfo),
    ];
  }

  generateFileFields(personInfo: PersonInfoFormData) {
    let withFiles = true;
    if (personInfo.roleType === PICType.BILLING && this.isPersonal)
      withFiles = !personInfo.isSameWithBizOwner;
    else withFiles = personInfo.directorOfCompany[0] !== "yes";

    if (withFiles) {
      return [
        {
          label: "Power of Attorney",
          type: ESummaryContentType.FILE,
          content: (personInfo.poaDoc as TFileUpload[]).map((el) =>
            initialFileTypeObj(el.file, el.signedUrl)
          ),
        },
        {
          label: "Identification Document",
          type: ESummaryContentType.FILE,
          content: (personInfo.idDoc as TFileUpload[]).map((el) =>
            initialFileTypeObj(el.file, el.signedUrl)
          ),
        },
      ];
    } else if (this.isJKTDashboard) {
      return [
        {
          label: "Identification Document",
          type: ESummaryContentType.FILE,
          content: (personInfo.dirIdDoc as TFileUpload[]).map((el) =>
            initialFileTypeObj(el.file, el.signedUrl)
          ),
        },
      ];
    } else if (
      this.currentClient.country == ECountryCode.PH &&
      !this.isPersonal &&
      this.isLocalDomicile
    ) {
      return [
        {
          label: "Power of Attorney",
          type: ESummaryContentType.FILE,
          content: (personInfo.poaDoc as TFileUpload[]).map((el) =>
            initialFileTypeObj(el.file, el.signedUrl)
          ),
        },
      ];
    } else {
      return [];
    }
  }

  async processRemoveForm(itemToRemove: string[]) {
    if (!itemToRemove.length) return;
    const { clientId } = this.$route.params;
    const promises = itemToRemove.map((id) =>
      this.removeFinancePICForm({
        clientId,
        picId: id,
      })
    );
    try {
      await Promise.all(promises);
    } catch (err) {
      console.error("Failed to remove Persons In-Charge:", err.message);
      throw new Error(err.message);
    }
  }

  _handleRawFormData(data: FormData): PersonInfo[] {
    let flattenFormData = Object.values(data).reduce((acc, cur) => {
      return [
        ...acc,
        ...cur.map((el: PersonInfoFormData, i: number) => ({
          ...el,
          picIndex: i,
          isDirector:
            this.isPersonal && el.roleType === PICType.BILLING && i === 0
              ? el.isSameWithBizOwner
              : el.directorOfCompany[0] === "yes",
        })),
      ];
    }, []);

    if (this.isPersonal) {
      flattenFormData = flattenFormData.filter(
        (item: PersonInfo) => item.roleType === PICType.BILLING
      );
    }

    return flattenFormData;
  }

  async onSubmit(data: FormData, dataToRemove: string[]) {
    this.errorMsg = null;
    this.loading = true;

    try {
      const { clientId } = this.$route.params;
      await Promise.all([
        this.processRemoveForm(dataToRemove),
        this.saveFinancePICForm({
          clientId,
          domicileType: this.currentClient.domicileType as EDomicileTypes,
          data: this._handleRawFormData(data),
        }),
      ]);
      this.showSummary = true;
      this.summaryData = this._handleSummary(data);
    } catch (err) {
      this.errorMsg = err.message;
      console.error("Failed to update Persons In-Charge info:", err.message);
    } finally {
      this.loading = false;
    }
  }

  onCancel() {
    const { clientId } = this.$route.params;
    this.$router.push(`/client/${clientId}`);
  }
}
